/**
 * Knowledge base service for company dashboard
 */

const API_BASE_URL = 'https://universal-ai-agent-backend.diypa571.workers.dev'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface KnowledgeEntry {
  id: number
  knowledge_id: string
  agent_id: number
  title: string
  content: string
  category?: string
  keywords?: string
  source: string
  is_active: boolean
  usage_count: number
  created_at: string
  updated_at: string
  assigned_agent_ids?: number[]  // Array of assigned agent IDs
}

class KnowledgeService {
  private getToken(): string | null {
    return localStorage.getItem('token')
  }

  private getHeaders(): HeadersInit {
    const token = this.getToken()
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  }

  /**
   * Get all knowledge entries for company
   */
  async getKnowledge(agentId?: number): Promise<ApiResponse<KnowledgeEntry[]>> {
    try {
      let url = `${API_BASE_URL}/api/knowledge`
      if (agentId) {
        url += `?agentId=${agentId}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch knowledge' }))
        return { success: false, error: error.message }
      }

      const data = await response.json()
      return { success: true, data: data.data }
    } catch (error) {
      console.error('Get knowledge error:', error)
      return { success: false, error: 'Failed to fetch knowledge' }
    }
  }

  /**
   * Add new knowledge entry
   */
  async addKnowledge(entry: {
    agentIds: number[]  // Array of agent IDs to assign
    title: string
    content: string
    category?: string
    keywords?: string
  }): Promise<ApiResponse<{ knowledgeId: string }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(entry)
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to add knowledge' }))
        return { success: false, error: error.message }
      }

      const data = await response.json()
      return { success: true, data: data.data }
    } catch (error) {
      console.error('Add knowledge error:', error)
      return { success: false, error: 'Failed to add knowledge' }
    }
  }

  /**
   * Update knowledge entry
   */
  async updateKnowledge(knowledgeId: string, updates: {
    title?: string
    content?: string
    category?: string
    keywords?: string
    isActive?: boolean
    agentIds?: number[]  // Optional: update agent assignments
  }): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updates)
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update knowledge' }))
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      console.error('Update knowledge error:', error)
      return { success: false, error: 'Failed to update knowledge' }
    }
  }

  /**
   * Delete knowledge entry
   */
  async deleteKnowledge(knowledgeId: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to delete knowledge' }))
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      console.error('Delete knowledge error:', error)
      return { success: false, error: 'Failed to delete knowledge' }
    }
  }
}

export const knowledgeService = new KnowledgeService()

